import { Component, OnInit, OnDestroy, AfterViewChecked, ElementRef, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { ChatRoom, ChatService, Message, MessageDTO, MessageRequest } from "../../_services/chat.service";
import { UserService } from "../../../services/user-service/user.service";
import { AppUserDTO } from "../../_models/userDTO";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { CommonResponse } from "../../_models/Response";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { IMessage } from "@stomp/stompjs";
import { CustomizerSettingsService } from "../../../customizer-settings/customizer-settings.service";
import { MatCard } from "@angular/material/card";
import { DialogService } from "../../_modal/dialog.service";
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule,MatCard]
})

export class ChatComponent implements OnInit, OnDestroy,AfterViewChecked {
    @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  ngAfterViewChecked() {
    this.scrollToBottom();
  }

    private scrollToBottom(): void {
    try {
      this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
    } catch(err) { 
      // Peut arriver si l'élément n'est pas encore chargé, ignorer
    }
  }

  currentUserId: number | null = null;
  isDarkMode: boolean = false;
  recipientId: number | null = null;
  recipientUsername: string | null = null;
  messageContent = '';
  messages: Message[] = [];
  availableUsers: AppUserDTO[] = [];
  unreadCounts: { [userId: number]: number } = {};
  searchTerm = '';
  lastMessagesByUser: { [userId: string]: MessageDTO } = {};
  lastMessagesByChatRoom: { [chatRoomId: number]: MessageDTO } = {};
  notificationMessage: string | null = null;

  private subscriptions: Subscription[] = [];
  private baseUrl = environment.base_url;
  private apiUrl = `${this.baseUrl}/api/chat`;

  constructor(
    private userService: UserService,
    private http: HttpClient,
    public customizer: CustomizerSettingsService,
    private snackBar: MatSnackBar,
    private chatService: ChatService,
    private dialogService: DialogService,       

  ) {}

  ngOnInit(): void {
    const userIdStr = sessionStorage.getItem('user_id');
    const username = sessionStorage.getItem('user_username');
    if (userIdStr && username) {
      this.currentUserId = Number(userIdStr);

      // Connexion WebSocket
      this.chatService.connect(username).then(() => {
        console.log('✅ WebSocket connecté');
        this.chatService.fetchUnreadMessages(this.currentUserId!);
      }).catch(err => console.error('❌ Erreur WebSocket:', err));

      // Abonnement aux messages
      this.subscriptions.push(
        this.chatService.onMessage().subscribe({
          next: msgs => {
            this.messages = msgs;
            this.loadLastMessages();
          },
          error: err => console.error('Erreur WebSocket:', err)
        })
      );

      // Abonnement aux compteurs de messages non lus
      this.subscriptions.push(
        this.chatService.unreadCounts$.subscribe(counts => {
          this.unreadCounts = counts;
        })
      );

    const notifSub = this.chatService.notificationReceived$.subscribe((notif) => {
      console.log('📣 Nouvelle notification reçue:', notif);

      // ✅ Méthode 1 : Snackbar Angular Material
      this.snackBar.open(notif || 'Nouveau message reçu', 'Fermer', {
        duration: 5000,
        panelClass: ['notification-snackbar']
      });
    });

    this.subscriptions.push(notifSub);
      // Chargement des utilisateurs disponibles
      this.subscriptions.push(
        this.userService.getUsers().subscribe({
          next: (res: CommonResponse) => {
            if (res.status) {
              this.availableUsers = (res.data as AppUserDTO[]).filter(u => u.id !== this.currentUserId);
              this.loadLastMessages();
            }
          },
          error: err => console.error('Erreur getUsers:', err)
        })
      );
    } else {
      console.error('❌ Aucun identifiant utilisateur trouvé dans sessionStorage');
    }
  }

sendMessageWithNotification(): void {
  if (!this.messageContent.trim() || this.recipientId == null || this.currentUserId == null) return;
  const roomId = this.generateRoomId(this.currentUserId, this.recipientId);
  const msgRequest: MessageRequest = {
    content: this.messageContent.trim(),
    senderId: this.currentUserId,
    recipientId: this.recipientId,
    roomId,
    timestamp: new Date().toISOString(),
    seen: false
  };

  // Utilisation de la nouvelle méthode avec notification
  this.chatService.sendMessageWithNotification(msgRequest);
  this.messageContent = '';
}


transformLastMessages(rawMessagesById: {[key: string]: MessageDTO}): {[userId: number]: MessageDTO} {
  const mapUserToMessage: {[userId: number]: MessageDTO} = {};

  Object.values(rawMessagesById).forEach(msg => {
    const otherUserId = msg.senderId === this.currentUserId ? msg.recipientId : msg.senderId;

    // On convertit les timestamps en Date, en tenant compte qu'ils peuvent être undefined
    const msgDate = msg.timestamp ? new Date(msg.timestamp) : new Date(0); // date très ancienne si undefined
    const existingDate = mapUserToMessage[otherUserId]?.timestamp ? new Date(mapUserToMessage[otherUserId].timestamp!) : new Date(0);

    if (!mapUserToMessage[otherUserId] || msgDate > existingDate) {
      mapUserToMessage[otherUserId] = msg;
    }
  });

  return mapUserToMessage;
}
shouldShowUnseenIndicator(otherUserId: number): boolean {
  const lastMessage = this.lastMessagesByUser[otherUserId];
  return !!lastMessage && !lastMessage.seen && lastMessage.senderId === otherUserId;
}

loadLastMessages(): void {
  if (this.currentUserId !== null) {
this.chatService.getLastMessages(this.currentUserId).subscribe({
  next: (data) => {
    this.lastMessagesByUser = this.transformLastMessages(data);
  },
  error: (err) => {
    console.error('Erreur lors du chargement des derniers messages', err);
  }
});

  } else {
    console.warn('Utilisateur non connecté, impossible de charger les messages.');
  }
}

onRecipientSelected(userId: number): void {
  this.recipientId = userId;
  const selectedUser = this.availableUsers.find(u => u.id === userId);
  this.recipientUsername = selectedUser?.username || null;

  const roomId = this.generateRoomId(this.currentUserId!, this.recipientId);
  this.chatService.joinRoom(roomId);

  // Marquer les messages comme lus côté backend
  this.http.put(`${this.apiUrl}/mark-seen?roomId=${roomId}&userId=${this.currentUserId}`, {}).subscribe({
    next: () => {
      console.log('📥 Messages marqués comme vus');
      this.loadLastMessages(); // 👈 Ici tu réactualises la liste à gauche par exemple

      this.chatService.clearUnreadCount(this.recipientId!); // ✅ Réinitialise le compteur pour ce user
    },
    error: err => console.error('Erreur mark-seen:', err)
  });

  this.chatService.getMessagesByRoom(roomId).subscribe({
    next: (msgs) => {
      this.messages = msgs.filter(m =>
        (m.sender?.id === this.currentUserId && m.recipient?.id === this.recipientId) ||
        (m.sender?.id === this.recipientId && m.recipient?.id === this.currentUserId)
      );
            this.loadLastMessages(); 

    },
    error: err => console.error('Erreur chargement historique:', err),
  });
}


sendMessage(): void {
  if (!this.messageContent.trim() || this.recipientId == null || this.currentUserId == null) return;

  const roomId = this.generateRoomId(this.currentUserId, this.recipientId);
  const msgRequest: MessageRequest = {
    content: this.messageContent.trim(),
    senderId: this.currentUserId,
    recipientId: this.recipientId,
    roomId,
    timestamp: new Date().toISOString(),
    seen: false
  };

  this.chatService.sendMessage(msgRequest);
  this.messageContent = '';
}
showDelete: any = null;
deleteMessage(messageId: number) {
  if (this.currentUserId === null) return; // sécurité

  this.dialogService.open(
    "Êtes-vous sûr de vouloir supprimer ce message ?",
    environment.warning_message_type,
    'danger',
    environment.warning_icon,
    true
  ).then((result: any) => {
    if (result) {
      this.chatService.delete(messageId, this.currentUserId!).subscribe(() => {
        this.messages = this.messages.filter(m => m.id !== messageId);
        this.loadLastMessages()
      });
    }
  });
}



  filteredUsers(): AppUserDTO[] {
    if (!this.searchTerm.trim()) return this.availableUsers;
    const term = this.searchTerm.toLowerCase();
    return this.availableUsers.filter(u => u.name.toLowerCase().includes(term));
  }

  get totalUnreadMessages(): number {
    return Object.values(this.unreadCounts).reduce((sum, count) => sum + count, 0);
  }

  private generateRoomId(user1: number, user2: number): string {
    return [user1, user2].sort().join('_');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.chatService.disconnect();
  }
}
